import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createApi, type ApiClient } from './api';
import { DEV_AUTH, cognitoLogin, cognitoLogout, devLogin, type AuthSession } from './auth';
import { I18nContext, translate, toLanguageCode, type Lang, type StringKey } from './i18n';
import { Chat, type ChatHandle } from './components/Chat';
import { AppointmentsCard } from './components/AppointmentsCard';
import { BookingCard } from './components/BookingCard';
import { RecordsCard } from './components/RecordsCard';

function readLang(): Lang {
  try { return localStorage.getItem('ampshift.portal.lang') === 'ar' ? 'ar' : 'en'; } catch { return 'en'; }
}
type Theme = 'system' | 'light' | 'dark';
function readTheme(): Theme {
  try { const t = localStorage.getItem('ampshift.portal.theme'); return t === 'dark' || t === 'light' ? t : 'system'; } catch { return 'system'; }
}

export function App() {
  const [lang, setLangState] = useState<Lang>(readLang);
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const cycleTheme = useCallback(() => {
    const next: Theme = theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system';
    setThemeState(next);
    try { localStorage.setItem('ampshift.portal.theme', next); } catch { /* ignore */ }
  }, [theme]);
  useEffect(() => {
    if (theme === 'system') delete document.documentElement.dataset['theme'];
    else document.documentElement.dataset['theme'] = theme;
  }, [theme]);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const chatRef = useRef<ChatHandle>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const langRef = useRef<Lang>(lang);
  sessionRef.current = session;
  langRef.current = lang;

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try { localStorage.setItem('ampshift.portal.lang', next); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const i18n = useMemo(() => ({ lang, dir: lang === 'ar' ? ('rtl' as const) : ('ltr' as const), t: (k: StringKey) => translate(lang, k), setLang }), [lang, setLang]);

  const api: ApiClient = useMemo(() => createApi({ auth: () => sessionRef.current?.headers ?? null, locale: () => toLanguageCode(langRef.current) }), []);

  useEffect(() => {
    if (!DEV_AUTH || session) return;
    setAuthBusy(true);
    devLogin().then(setSession).catch((e: unknown) => setAuthError(e instanceof Error ? e.message : String(e))).finally(() => setAuthBusy(false));
  }, [session]);

  const submitLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setAuthBusy(true);
    setAuthError(null);
    try {
      setSession(await cognitoLogin(String(form.get('email') ?? ''), String(form.get('password') ?? '')));
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : String(err));
    } finally {
      setAuthBusy(false);
    }
  };

  const signOut = () => { cognitoLogout(); setSession(null); };
  const changed = useCallback(() => setRefreshKey((k) => k + 1), []);
  const chat = useCallback((text: string) => chatRef.current?.send(text) ?? Promise.resolve(), []);

  return (
    <I18nContext.Provider value={i18n}>
      <div className="app">
        <header className="app-header">
          <div className="brand"><img className="brand-mark" src="/ampshift-logo.svg" alt="" aria-hidden="true" width={36} height={36} /><h1 style={{ fontSize: '1.1rem', margin: 0 }}>{i18n.t('appName')}</h1></div>
          <div className="header-actions">
            {session && <span className="muted">{session.displayName}</span>}
            <button className="btn btn-sm" onClick={cycleTheme} aria-label={i18n.t('theme')} title={`${i18n.t('theme')}: ${i18n.t(theme === 'dark' ? 'themeDark' : theme === 'light' ? 'themeLight' : 'themeSystem')}`}>{theme === 'dark' ? '☾' : theme === 'light' ? '☀' : '◐'}</button>
            <button className="btn btn-sm" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} aria-label="language" lang={lang === 'ar' ? 'en' : 'ar'}>{i18n.t('language')}</button>
            {session && <button className="btn btn-sm" onClick={signOut}>{i18n.t('signOut')}</button>}
          </div>
        </header>
        <div className="banner">{i18n.t('syntheticBanner')}</div>
        {!session ? (
          <section className="card login">
            <h2>{i18n.t('signIn')}</h2>
            {DEV_AUTH ? <div className="muted">{i18n.t('devSignIn')}</div> : (
              <form className="stack" onSubmit={(e) => void submitLogin(e)}>
                <label className="field">{i18n.t('email')}<input name="email" type="email" autoComplete="username" required /></label>
                <label className="field">{i18n.t('password')}<input name="password" type="password" autoComplete="current-password" required /></label>
                <button className="btn btn-primary" type="submit" disabled={authBusy}>{authBusy ? i18n.t('signingIn') : i18n.t('signIn')}</button>
              </form>
            )}
            {authError && <div className="err" role="alert">{authError}</div>}
          </section>
        ) : (
          <main className="layout">
            <div className="column">
              <Chat ref={chatRef} api={api} onDataChanged={changed} />
              <RecordsCard api={api} />
            </div>
            <div className="column">
              <AppointmentsCard api={api} refreshKey={refreshKey} onChanged={changed} onChat={chat} />
              <BookingCard api={api} onBooked={changed} />
            </div>
          </main>
        )}
      </div>
    </I18nContext.Provider>
  );
}
